/* eslint-disable react/prop-types */
import TipsAndUpdatesIcon from "@mui/icons-material/TipsAndUpdates";
import { CircularProgress, Tooltip } from "@mui/material";
import { Formik } from "formik";
import { useEffect, useState } from "react";
import characterStore from "../../../store/CharactersStore";
import { PrimaryButton } from "../../Button/PrimaryButton";
import { TransparentButton } from "../../Button/TransparentButton";
import FormInput from "../../FormInput";
import { Title } from "../../molecules/header";
import { SubTitle } from "../../molecules/subtitle";
import { Suggestions } from "./Suggestions";
import { validationSchema } from "./validationSchema";
import { useSuggestionsHook } from "./useSuggestionHook";

const CharacterForm = ({
  characterDetails,
  type,
  onClose,
  overrideOnSubmit,
}) => {
  const [
    humanData,
    createNewCharacter,
    updateCharacter,
    selectedKidForCharacter,
    characterUpdating,
    getHumanData,
  ] = characterStore((state) => [
    state.humanData,
    state.createNewCharacter,
    state.updateCharacter,
    state.selectedKidForCharacter,
    state.characterUpdating,
    state.getHumanData,
  ]);
  const isUpdate = Boolean(characterDetails);
  const isHuman = type === "human";
  const isImageAnalysing = humanData?.is_prediction_pending;
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { loading, suggestions, fetchSuggestions } = useSuggestionsHook();

  useEffect(() => {
    if (isHuman && selectedKidForCharacter) {
      getHumanData();
    }
  }, [selectedKidForCharacter, humanData?.is_prediction_pending]);

  if (isHuman && !humanData) {
    return (
      <div className="min-h-[80vh] w-full flex flex-col justify-center items-center">
        <CircularProgress />
        <Title> Loading...</Title>
      </div>
    );
  }
  return (
    <Formik
      validateOnChange={true}
      validateOnMount={true}
      enableReinitialize={true}
      initialValues={{
        age:
          characterDetails?.age || (isHuman && humanData?.human?.age) || null,
        gender:
          characterDetails?.gender ||
          (isHuman && humanData?.human?.gender) ||
          null,
        name:
          characterDetails?.name || (isHuman && humanData?.human?.name) || "",
        appearance:
          characterDetails?.visual_description_plaintext ||
          (isHuman && humanData?.human?.visual_description_plaintext) ||
          "",
        typical_expression: characterDetails?.typical_expression || "",
        typical_clothing: characterDetails?.typical_clothing || "",
      }}
      onSubmit={(values) => {
        if (overrideOnSubmit) {
          overrideOnSubmit(values);
          return;
        }
        const characterType = characterDetails?.type || type;
        const body = {
          ...values,
          type: characterType || "others",
        };
        if (characterType === "human") {
          body.human_id = selectedKidForCharacter.id;
        }

        if (isUpdate) {
          updateCharacter(characterDetails.id, body, () => {
            onClose();
          });
        } else {
          createNewCharacter(body, () => {
            onClose();
          });
        }
      }}
      validationSchema={validationSchema}
    >
      {({ handleSubmit, isSubmitting, values, setFieldValue }) => {
        return (
          <div className="p-8 relative w-full">
            {isImageAnalysing && (
              <div className="absolute top-0 left-0 backdrop-blur-sm bg-white/30 flex flex-col justify-center items-center w-full h-full z-40 ">
                <div className="m-auto w-[80%] md:w-3/4 bg-white rounded-2xl p-4 opacity-100 drop-shadow-md">
                  <SubTitle> Please Wait..</SubTitle>
                  <br></br>
                  <SubTitle>
                    {" "}
                    Your image is being analysed. This may take few seconds..
                  </SubTitle>
                </div>
              </div>
            )}
            <FormInput label="Name" name={`name`} placeholder="Name" />
            <div className="relative">
              <FormInput
                label="Appearance"
                name={`appearance`}
                placeholder="Appearance"
                type="textarea"
                rows="3"
              />
              <Tooltip
                title="Use AI-suggested details for better character consistency."
                arrow
                placement="top"
              >
                <div
                  onClick={() => {
                    setShowSuggestions(true);
                    fetchSuggestions(values?.appearance);
                  }}
                  className={`${values.appearance ? "block" : "hidden"} ${
                    showSuggestions ? "animate-none" : "animate-bounce"
                  } absolute top-10 right-5 z-50 text-yellow-400  cursor-pointer hover:drop-shadow-[0px_-10px_5px_rgba(250,204,21,1)] hover:animate-none `}
                >
                  <TipsAndUpdatesIcon />
                </div>
              </Tooltip>
              {showSuggestions && (
                <Suggestions
                  onReject={() => {
                    setShowSuggestions(false);
                  }}
                  onAccept={() => {
                    setFieldValue(
                      "appearance",
                      suggestions?.visual_description
                    );
                    setFieldValue(
                      "typical_clothing",
                      suggestions?.typical_clothing
                    );
                    setFieldValue(
                      "typical_expression",
                      suggestions?.typical_expression
                    );
                    setShowSuggestions(false);
                  }}
                  isLoading={loading}
                  suggestedData={suggestions}
                />
              )}
            </div>
            <FormInput
              label="Typical clothing"
              name={`typical_clothing`}
              placeholder="Gretel is dressed in a knee-length earthy-toned dress with a white apron, simple brown leather shoes, and a small bonnet or headscarf"
              type="textarea"
              rows="3"
            />
            <FormInput
              label="Typical Expression"
              name={`typical_expression`}
              placeholder="Typical expression"
              type="textarea"
              rows="3"
            />
            <div className="flex justify-around">
              <TransparentButton
                handleButtonClick={() => {
                  onClose();
                }}
              >
                Cancel
              </TransparentButton>
              <PrimaryButton
                disabled={characterUpdating || isSubmitting}
                type="submit"
                className={"min-w-[200px]"}
                handleButtonClick={() => {
                  handleSubmit();
                }}
              >
                {isSubmitting || characterUpdating ? (
                  <CircularProgress size={22} />
                ) : isUpdate ? (
                  <span>Update</span>
                ) : (
                  <span>Create New</span>
                )}
              </PrimaryButton>
            </div>
          </div>
        );
      }}
    </Formik>
  );
};
export default CharacterForm;
